// Single source of truth for client-side upload size limits, so the Settings
// overview and the enforcing call sites can't drift apart.
//
// Every upload in the app is base64-inlined into a GraphQL mutation body and
// rides the BFF GraphQL proxy, which caps the request body at
// GRAPHQL_BODY_LIMIT (16 MB, see apps/bff/src/limits.ts). base64 inflates a
// payload by ~33 %, so the per-file caps below sit safely under that ceiling.

export const MB = 1024 * 1024;

/** BFF GraphQL body cap (apps/bff/src/limits.ts GRAPHQL_BODY_LIMIT). */
export const BFF_GRAPHQL_BODY_LIMIT = 16 * MB;

/** Save · Service document upload (enforced in ServicePage). */
export const MAX_DOCUMENT_UPLOAD_BYTES = 10 * MB;
/** Share item cover image. */
export const MAX_SHARE_IMAGE_BYTES = 5 * MB;
/**
 * Share item attachment. The BFF body cap (16 MB) minus base64 inflation
 * (~33 %) and headroom for the rest of the mutation payload.
 */
export const MAX_SHARE_ATTACHMENT_BYTES = 11 * MB;
/** Profile photo / avatar. */
export const MAX_AVATAR_BYTES = 5 * MB;

export interface UploadLimit {
  /** i18n id: where in the app this applies. */
  areaId: string;
  /** i18n id: what is uploaded there. */
  whatId: string;
  /** i18n id: human-readable limit. */
  limitId: string;
  /** i18n id: optional clarification. */
  noteId?: string;
}

// Display list for the Settings overview. Keep in sync with the constants
// above and the enforcing components. Strings live in i18n under
// settings.upload.*; resolved at render in SettingsPage.
export const UPLOAD_LIMITS: readonly UploadLimit[] = [
  {
    areaId: "settings.upload.docs.area",
    whatId: "settings.upload.docs.what",
    limitId: "settings.upload.docs.limit",
  },
  {
    areaId: "settings.upload.shareImage.area",
    whatId: "settings.upload.shareImage.what",
    limitId: "settings.upload.shareImage.limit",
  },
  {
    areaId: "settings.upload.shareAttach.area",
    whatId: "settings.upload.shareAttach.what",
    limitId: "settings.upload.shareAttach.limit",
    noteId: "settings.upload.shareAttach.note",
  },
  {
    areaId: "settings.upload.profile.area",
    whatId: "settings.upload.profile.what",
    limitId: "settings.upload.profile.limit",
  },
  {
    areaId: "settings.upload.appearance.area",
    whatId: "settings.upload.appearance.what",
    limitId: "settings.upload.appearance.limit",
    noteId: "settings.upload.appearance.note",
  },
];

/** i18n id for the general footnote under the upload-limits overview. */
export const UPLOAD_LIMIT_NOTE_ID = "settings.upload.generalNote";
